import { useCallback, useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import { MessageDescriptor } from "@lingui/core";
import {
  MappedSize,
  MappedSizeMapping,
  ProductVariation,
} from "src/types/graphql.d";
import { getSchemaOrder } from "src/utils/sizes/size-meta";
import {
  normalizeUnit,
  buildGroupTitle,
  unitRank,
  formatSizeValue,
} from "src/molecules/Filter/components/Sizes/utils/sizeSchema";
import { getStorageItem, setStorageItem } from "src/utils/helper/localStorage";
import { SIZE_SCHEMA_STORAGE_KEY } from "src/molecules/SizeSelector/constants";

function getEffectiveMappings(
  mappedSize?: MappedSize | null
): MappedSizeMapping[] {
  if (!mappedSize) return [];
  if (mappedSize.irregular && mappedSize.fallback?.mappings) {
    return mappedSize.fallback.mappings;
  }
  return mappedSize.mappings || [];
}

export type SizeSchemaMappingType = {
  key: string;
  schema: string;
  unit?: string;
  title: string;
};

export interface SizeMapping {
  variationId: string;
  brandSize: string;
  brandSchema?: string | null;
  brandUnit?: string | null;
  stockQuantity: number;
  mappings: MappedSizeMapping[];
}

export const sizeFitLabelMessages: Record<string, MessageDescriptor> = {
  runs_small: msg`size_fit_label.runs_small`,
  runs_large: msg`size_fit_label.runs_large`,
};

export const getSizeStock = (quantity: number) => {
  if (quantity === 0) {
    return (
      <span>
        <Trans>Notify me</Trans>
      </span>
    );
  } else if (quantity === 1) {
    return (
      <span>
        <Trans>Last 1</Trans>
      </span>
    );
  } else if (quantity === 2) {
    return (
      <span>
        <Trans>Last 2</Trans>
      </span>
    );
  } else if (quantity === 3) {
    return (
      <span>
        <Trans>Last 3</Trans>
      </span>
    );
  }
  return null;
};

export function getTranslatedSize(
  mappings: MappedSizeMapping[],
  targetSchema: string,
  targetUnit?: string
): MappedSizeMapping | null {
  const normalizedTargetUnit = targetUnit
    ? normalizeUnit(targetUnit)
    : undefined;

  return (
    mappings.find((mapping) => {
      const schemaMatch =
        mapping.schema?.toUpperCase() === targetSchema.toUpperCase();
      if (!schemaMatch) return false;

      if (!normalizedTargetUnit) {
        const mappingUnit = normalizeUnit(mapping.unit || undefined);
        return !mappingUnit;
      }

      const mappingUnit = normalizeUnit(mapping.unit || undefined);
      return mappingUnit === normalizedTargetUnit;
    }) ||
    mappings.find(
      (mapping) => mapping.schema?.toUpperCase() === targetSchema.toUpperCase()
    ) ||
    null
  );
}

export function getAllTranslatedSizes(
  mappings: MappedSizeMapping[],
  targetSchema: string,
  targetUnit?: string
): MappedSizeMapping[] {
  const normalizedTargetUnit = targetUnit
    ? normalizeUnit(targetUnit)
    : undefined;

  let results = mappings.filter((mapping) => {
    const schemaMatch =
      mapping.schema?.toUpperCase() === targetSchema.toUpperCase();
    if (!schemaMatch) return false;

    if (!normalizedTargetUnit) {
      const mappingUnit = normalizeUnit(mapping.unit || undefined);
      return !mappingUnit;
    }

    const mappingUnit = normalizeUnit(mapping.unit || undefined);
    return mappingUnit === normalizedTargetUnit;
  });

  if (results.length === 0) {
    results = mappings.filter(
      (mapping) => mapping.schema?.toUpperCase() === targetSchema.toUpperCase()
    );
  }

  const seen = new Set<string>();
  return results.filter((mapping) => {
    if (seen.has(mapping.value)) return false;
    seen.add(mapping.value);
    return true;
  });
}

interface UseSizeSchemaFilterOptions {
  variations: ProductVariation[];
  locale?: string;
  selectedSchema?: string;
  onSchemaChange?: (schema: string) => void;
}

interface UseSizeSchemaFilterResult {
  availableSchemaMappings: SizeSchemaMappingType[];
  selectedSizeSchemaMapping: SizeSchemaMappingType | undefined;
  sizeMappings: SizeMapping[];
  hasMultipleSchemas: boolean;
  selectedSchema: string;

  handleSelectSchema: (schemaKey: string) => void;
  formatBrandSize: (sizeMap: SizeMapping) => string;
  formatTranslatedSize: (sizeMap: SizeMapping, showUnit?: boolean) => string;
  formatVariationValue: (
    variationId: string,
    fallbackValue?: string,
    fallbackSchema?: string
  ) => string;
}

export function useSizeSchemaFilter({
  variations,
  locale,
  selectedSchema: externalSchema,
  onSchemaChange,
}: UseSizeSchemaFilterOptions): UseSizeSchemaFilterResult {
  const { i18n } = useLingui();
  const [internalSchema, setInternalSchema] = useState<string>(() => {
    const saved = getStorageItem(SIZE_SCHEMA_STORAGE_KEY);
    return saved || "INT";
  });

  const effectiveLocale = locale || i18n.locale;
  const sizeLabel = i18n._("Size");

  const availableSchemaMappings = useMemo(() => {
    const brandSchema = variations[0]?.mapped_size?.schema?.toUpperCase();
    const schemaOrder = getSchemaOrder(effectiveLocale);

    const options = variations
      .flatMap((variation) => getEffectiveMappings(variation.mapped_size))
      .reduce<SizeSchemaMappingType[]>((schemas, mapping) => {
        if (!mapping.schema) return schemas;

        const schema = mapping.schema;
        if (schema.toUpperCase() === brandSchema) return schemas;

        const unit = normalizeUnit(mapping.unit || undefined);
        const key = unit ? `${schema}|${unit}` : schema;

        if (schemas.some((s) => s.key === key)) return schemas;

        schemas.push({
          key,
          schema,
          unit,
          title: buildGroupTitle({ schema, unit, sizeLabel }),
        });

        return schemas;
      }, []);

    options.sort((a, b) => {
      const aSchemaIdx = schemaOrder.indexOf(a.schema);
      const bSchemaIdx = schemaOrder.indexOf(b.schema);
      const aIdx = aSchemaIdx === -1 ? Number.MAX_SAFE_INTEGER : aSchemaIdx;
      const bIdx = bSchemaIdx === -1 ? Number.MAX_SAFE_INTEGER : bSchemaIdx;

      if (aIdx !== bIdx) return aIdx - bIdx;
      return unitRank(a.unit) - unitRank(b.unit);
    });

    return options;
  }, [variations, effectiveLocale, sizeLabel]);

  const selectedSchema = useMemo(() => {
    const preferredSchema = externalSchema ?? internalSchema;
    const isAvailable = availableSchemaMappings.some(
      (m) => m.key === preferredSchema
    );
    if (isAvailable) return preferredSchema;
    return availableSchemaMappings[0]?.key || "INT";
  }, [externalSchema, internalSchema, availableSchemaMappings]);

  const selectedSizeSchemaMapping = useMemo(
    () => availableSchemaMappings.find((m) => m.key === selectedSchema),
    [availableSchemaMappings, selectedSchema]
  );

  const sizeMappings: SizeMapping[] = useMemo(() => {
    return variations
      .filter((v) => v.mapped_size)
      .map((variation) => ({
        variationId: variation.id,
        brandSize: variation.mapped_size?.value || variation.size || "",
        brandSchema: variation.mapped_size?.schema,
        brandUnit: variation.mapped_size?.unit,
        stockQuantity: variation.stock_quantity,
        mappings: getEffectiveMappings(variation.mapped_size),
      }));
  }, [variations]);

  const handleSelectSchema = useCallback(
    (schemaKey: string) => {
      if (onSchemaChange) {
        onSchemaChange(schemaKey);
      } else {
        setInternalSchema(schemaKey);
      }
      setStorageItem(SIZE_SCHEMA_STORAGE_KEY, schemaKey);
    },
    [onSchemaChange]
  );

  const formatBrandSize = useCallback((sizeMap: SizeMapping) => {
    return formatSizeValue(
      sizeMap.brandSize,
      sizeMap.brandSchema,
      sizeMap.brandUnit,
      true
    );
  }, []);

  const formatTranslatedSize = useCallback(
    (sizeMap: SizeMapping, showUnit = false) => {
      const allTranslated = getAllTranslatedSizes(
        sizeMap.mappings,
        selectedSizeSchemaMapping?.schema || "INT",
        selectedSizeSchemaMapping?.unit
      );
      if (allTranslated.length === 0) return "";

      return allTranslated
        .map((translated) =>
          formatSizeValue(
            translated.value,
            selectedSizeSchemaMapping?.schema,
            translated.unit,
            showUnit
          )
        )
        .join(" / ");
    },
    [selectedSizeSchemaMapping]
  );

  const formatVariationValue = useCallback(
    (id: string, fallback = "", schema?: string) => {
      const map = sizeMappings.find((m) => m.variationId === id);
      if (map) {
        const brand = formatBrandSize(map);
        const translated = formatTranslatedSize(map, true);
        return translated ? `${brand} / ${translated}` : brand;
      }
      const label = selectedSizeSchemaMapping?.schema || schema;
      return label ? `${fallback} ${label}` : fallback;
    },
    [
      sizeMappings,
      formatBrandSize,
      formatTranslatedSize,
      selectedSizeSchemaMapping,
    ]
  );

  const hasMultipleSchemas = availableSchemaMappings.length > 1;

  return {
    availableSchemaMappings,
    selectedSizeSchemaMapping,
    sizeMappings,
    hasMultipleSchemas,
    selectedSchema,
    handleSelectSchema,
    formatBrandSize,
    formatTranslatedSize,
    formatVariationValue,
  };
}
